import { useState } from "react";
import { Check, Edit2, ShieldOff, Trash2, UserPlus, Users, X } from "lucide-react";

import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";

import {
  useAddGroupMember,
  useGetGroupMembers,
  useRemoveGroupMember,
  useUpdateChatGroup,
} from "../hooks/use-chat-groups";
import { ScrollArea } from "./ui/scroll-area";

const ACCESS_DENIED_MESSAGE =
  "You don't have access to manage this group.";

export function ChannelMembers({ channelId, channelName, onClose }) {
  const [showAddMember, setShowAddMember] = useState(false);
  // Inline notice shown above the available-staff list when the API
  // returns 409 (staff is already a member). Cleared on the next add
  // attempt so a successful follow-up doesn't leave stale text behind.
  const [addInlineNotice, setAddInlineNotice] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(channelName || "");
  const [pendingRemoveId, setPendingRemoveId] = useState(null);

  const { data: staffMembers } = useStaffMembers();
  const { data: me } = useStaffInfo();
  const myStaffId = me?.staffMember?.id;
  const {
    data: members,
    isLoading,
    error: membersError,
  } = useGetGroupMembers(channelId);
  const addMember = useAddGroupMember();
  const removeMember = useRemoveGroupMember();
  const updateChannel = useUpdateChatGroup();

  // The members endpoint enforces membership. A 403 here means we're
  // looking at a group the caller isn't actually in (stale cache,
  // shared link, etc.) — render the access-denied state and hide all
  // management controls.
  const isAccessDenied = membersError?.response?.status === 403;

  // Get staff members who are not already in the channel
  const availableStaff = staffMembers?.filter(
    (staff) => !members?.some((member) => {
      const memberStaffId = member.staffMemberId || member.staffId || member.id;

      return String(memberStaffId) === String(staff.id);
    })
  ) || [];

  const enrichedMembers = members?.map((member) => {
    const memberStaffId =
      member.staffMemberId || member.staffId || member.staff_id || member.id;

    const nestedStaff = member.staffMember;
    const staffInfo =
      nestedStaff ||
      staffMembers?.find(
        (s) =>
          String(s.id) === String(memberStaffId) ||
          String(s.staffId) === String(memberStaffId)
      );

    const firstName = staffInfo?.firstName || member.firstName || member.first_name || "";
    const lastName = staffInfo?.lastName || member.lastName || member.last_name || "";

    return {
      ...member,
      staffId: memberStaffId,
      memberRecordId: member.id || memberStaffId,
      firstName,
      lastName,
      displayName:
        firstName || lastName
          ? `${firstName} ${lastName}`.trim()
          : `Member ${memberStaffId}`,
      staffRole: staffInfo?.staffRole || member.staffRole || member.staff_role || "",
      active: staffInfo?.active ?? member.active ?? false,
    };
  }) || [];

  const handleAddMember = (staffId) => {
    setAddInlineNotice(null);
    addMember.mutate(
      { groupId: channelId, staffMemberId: staffId },
      {
        onSuccess: () => {
          setShowAddMember(false);
        },
        onError: (error) => {
          const status = error?.response?.status;
          if (status === 409) {
            setAddInlineNotice("That person is already a member.");
            return;
          }
          if (status === 403) {
            setAddInlineNotice(ACCESS_DENIED_MESSAGE);
            setShowAddMember(false);
          }
        },
      }
    );
  };

  const handleRemoveMember = (memberRecordId) => {
    setPendingRemoveId(memberRecordId);
    removeMember.mutate(
      { groupId: channelId, memberId: memberRecordId },
      {
        onSettled: () => {
          setPendingRemoveId(null);
        },
      }
    );
  };

  const handleRename = () => {
    const next = nameDraft.trim();
    if (!next || next === channelName) {
      setRenaming(false);
      setNameDraft(channelName || "");
      return;
    }
    updateChannel.mutate(
      { groupId: channelId, name: next },
      {
        onSettled: () => setRenaming(false),
      }
    );
  };

  return (
    <div
      style={{
        position: "absolute",
        top: "100%",
        right: 0,
        marginTop: "4px",
        width: "300px",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        border: "1px solid #e5e7eb",
        zIndex: 100,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: "#f9fafb",
          gap: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: 0 }}>
          <Users style={{ height: "16px", width: "16px", color: "#6b7280", flexShrink: 0 }} />
          {renaming && !isAccessDenied ? (
            <input
              type="text"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRename();
                if (e.key === "Escape") {
                  setRenaming(false);
                  setNameDraft(channelName || "");
                }
              }}
              autoFocus
              disabled={updateChannel.isPending}
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: "0.875rem",
                fontWeight: 600,
                padding: "2px 6px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                outline: "none",
              }}
            />
          ) : (
            <span
              style={{
                fontWeight: 600,
                fontSize: "0.875rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={channelName}
            >
              # {channelName}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
          {!isAccessDenied && !renaming && (
            <button
              onClick={() => {
                setNameDraft(channelName || "");
                setRenaming(true);
              }}
              title="Rename channel"
              aria-label="Rename channel"
              style={{
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
              }}
            >
              <Edit2 style={{ height: "14px", width: "14px", color: "#6b7280" }} />
            </button>
          )}
          {!isAccessDenied && renaming && (
            <button
              onClick={handleRename}
              disabled={updateChannel.isPending}
              title="Save name"
              aria-label="Save name"
              style={{
                border: "none",
                backgroundColor: "transparent",
                cursor: updateChannel.isPending ? "default" : "pointer",
                padding: "4px",
                display: "flex",
              }}
            >
              <Check style={{ height: "14px", width: "14px", color: "#22c55e" }} />
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              border: "none",
              backgroundColor: "transparent",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
            }}
          >
            <X style={{ height: "16px", width: "16px", color: "#6b7280" }} />
          </button>
        </div>
      </div>

      {isAccessDenied ? (
        <div
          style={{
            padding: "24px 16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "8px",
            color: "#6b7280",
          }}
        >
          <ShieldOff style={{ height: "24px", width: "24px", color: "#9ca3af" }} />
          <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#374151" }}>
            {ACCESS_DENIED_MESSAGE}
          </span>
          <span style={{ fontSize: "0.75rem" }}>
            Ask an existing member to add you to this channel.
          </span>
        </div>
      ) : (
        <>
          {/* Add Member Button */}
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>
            <button
              onClick={() => {
                setShowAddMember((value) => !value);
                setAddInlineNotice(null);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
                padding: "8px 12px",
                border: "1px dashed #d1d5db",
                borderRadius: "6px",
                backgroundColor: showAddMember ? "#f3f4f6" : "transparent",
                cursor: "pointer",
                color: "#1164a3",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              <UserPlus style={{ height: "16px", width: "16px" }} />
              Add people
            </button>
          </div>

          {/* Add Member Dropdown */}
          {showAddMember && (
            <div style={{ borderBottom: "1px solid #e5e7eb" }}>
              {addInlineNotice && (
                <div
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#fef3c7",
                    color: "#92400e",
                    fontSize: "0.75rem",
                    borderBottom: "1px solid #fde68a",
                  }}
                >
                  {addInlineNotice}
                </div>
              )}
              <div
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <div style={{ padding: "8px" }}>
                  {availableStaff.length === 0 ? (
                    <div
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        color: "#9ca3af",
                        fontSize: "0.8125rem",
                      }}
                    >
                      All team members are already in this channel
                    </div>
                  ) : (
                    availableStaff.map((staff) => (
                      <button
                        key={staff.id}
                        onClick={() => handleAddMember(staff.id)}
                        disabled={addMember.isPending}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          width: "100%",
                          padding: "8px 12px",
                          border: "none",
                          borderRadius: "6px",
                          backgroundColor: "transparent",
                          cursor: addMember.isPending ? "not-allowed" : "pointer",
                          textAlign: "left",
                          fontSize: "0.875rem",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <span
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: staff.active ? "#22c55e" : "#9ca3af",
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ flex: 1 }}>
                          {staff.firstName} {staff.lastName}
                        </span>
                        {staff.staffRole && (
                          <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                            {staff.staffRole.toLowerCase()}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Current Members List */}
          <ScrollArea style={{ maxHeight: "200px" }}>
            <div style={{ padding: "8px" }}>
              {isLoading ? (
                <div
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: "0.8125rem",
                  }}
                >
                  Loading members...
                </div>
              ) : enrichedMembers.length === 0 ? (
                <div
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: "0.8125rem",
                  }}
                >
                  No members yet. Add people to this channel.
                </div>
              ) : (
                enrichedMembers.map((member) => {
                  const isMe =
                    myStaffId != null &&
                    Number(member.staffId) === Number(myStaffId);
                  const removeId = member.memberRecordId;
                  const isRemoving =
                    pendingRemoveId === removeId && removeMember.isPending;

                  return (
                    <div
                      key={removeId || member.staffId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 12px",
                        borderRadius: "6px",
                      }}
                    >
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: member.active ? "#22c55e" : "#9ca3af",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ flex: 1, fontSize: "0.875rem" }}>
                        {member.displayName ||
                          `${member.firstName} ${member.lastName}`.trim() ||
                          "Unknown Member"}
                        {isMe && (
                          <span
                            style={{
                              marginLeft: "6px",
                              fontSize: "0.6875rem",
                              color: "#6b7280",
                              fontWeight: 500,
                            }}
                          >
                            (you)
                          </span>
                        )}
                      </span>
                      {member.staffRole && (
                        <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                          {member.staffRole?.toLowerCase()}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (!removeId) return;
                          // Confirm only for self-removal since that
                          // boots you out of the channel; member-on-
                          // member removes are reversible by re-adding.
                          if (isMe) {
                            const ok = window.confirm(
                              "Leave this channel? You'll need to be re-added by another member."
                            );
                            if (!ok) return;
                          }
                          handleRemoveMember(removeId);
                        }}
                        disabled={isRemoving || !removeId}
                        title={isMe ? "Leave channel" : "Remove from channel"}
                        aria-label={isMe ? "Leave channel" : "Remove from channel"}
                        style={{
                          border: "none",
                          backgroundColor: "transparent",
                          cursor: isRemoving || !removeId ? "default" : "pointer",
                          padding: "4px",
                          display: "flex",
                          alignItems: "center",
                          opacity: isRemoving ? 0.5 : 1,
                          color: "#dc2626",
                        }}
                      >
                        <Trash2 style={{ height: "14px", width: "14px" }} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </>
      )}
    </div>
  );
}
